<script lang="ts" setup>
    import { computed } from 'vue';
    import { BookmakerDictionaryItem } from '@/models/dictionary/bookmaker';
    import sortBy from 'lodash-es/sortBy';
    import indexOf from 'lodash-es/indexOf';
    import { useBookmakerDictionary } from '@/stores';
    import { IToolUserSettings } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { Bookmaker, BookmakerRulesLink, BookmakerTermsLink } from '@/constants';
    import { useUserStore } from '@/stores/user';
    import { useAppStore } from '@/stores/app';
    import { userService } from '@/services/userService';
    import { cloneDeep } from 'lodash-es';
    import { useTheme } from 'vuetify';

    const { toolUserSettingsDialog } = defineProps<{
        toolUserSettingsDialog: IToolUserSettings;
    }>();

    const { t } = useI18n();
    const userStore = useUserStore();
    const appStore = useAppStore();
    const theme = useTheme();

    const bookmakerDictionary = useBookmakerDictionary();

    const bookmakerListOrdered = computed<BookmakerDictionaryItem[]>(() => {
        let order = [Bookmaker.Pinna, Bookmaker.Betfair, Bookmaker.Isn, Bookmaker.ThreeBet, Bookmaker.Sharpbet, Bookmaker.Ibc];
        const items = sortBy(bookmakerDictionary.items, function (obj: any) {
            return indexOf(order, obj.site_id);
        });

        return items.filter((bookmaker) => bookmaker.isVisibleForPlayer && bookmaker.isActive);
    });

    function minOrderStake(bookmakerId: number): number | null {
        const match = appStore.betOrderMinStakes.find(([id]) => id === bookmakerId);
        return match ? match[1] : null;
    }

    async function changeOrderSettings(): Promise<void> {
        await userService.changeSettings(toolUserSettingsDialog);
        userStore.settings = cloneDeep(toolUserSettingsDialog);
    }
</script>
<template>
    <v-container class="tab-item-wrapper pa-3">
        <v-row v-for="(bookmaker, index) in bookmakerListOrdered" :key="index" class="mt-1">
            <v-col class="py-1 pl-3 pr-1 my-auto" cols="4" style="height: 56px">
                <v-switch
                    density="compact"
                    width="120"
                    v-model="toolUserSettingsDialog.bet_order_site_ids_allowed"
                    :value="bookmaker.id"
                    color="tbGreen"
                    class="mb-2 mt-1"
                    :hide-details="true"
                    hide-spin-buttons
                    :ripple="false"
                    false-value="false"
                    @click.stop
                    @change="changeOrderSettings()"
                >
                    <template v-slot:label>
                        <span :class="['bookmaker' + bookmaker.id, 'font14']">
                            {{ bookmakerDictionary.nameById(bookmaker.id) }}
                        </span>
                    </template>
                </v-switch>
            </v-col>
            <v-col class="py-2 px-1 my-auto text-start" cols="4">
                <span class="font14" :class="theme.name.value === 'dark' ? 'lightGreyText' : 'blackText'">
                    {{ t('menu.orderSettingsMin') }}
                    {{ userStore.currency.symbol }}{{ minOrderStake(bookmaker.id) }}
                </span>
            </v-col>
            <v-col class="py-2 px-1 my-auto text-end">
                <span v-if="bookmaker.id in BookmakerRulesLink" class="font14 lightGreyText">
                    <a :href="BookmakerRulesLink[bookmaker.id]" target="_blank">
                        {{ t('menu.orderSettingsRules') }}
                    </a>
                </span>
            </v-col>
            <v-col class="py-2 px-1 my-auto text-end">
                <span v-if="bookmaker.id in BookmakerTermsLink" class="font14 lightGreyText">
                    <a :href="BookmakerTermsLink[bookmaker.id]" target="_blank">
                        {{ t('menu.orderSettingsTC') }}
                    </a>
                </span>
            </v-col>
        </v-row>
        <v-row>
            <p class="pt-0 pl-2 my-0 font13 font-weight-regular orangeText" style="text-align: justify">
                {{ t('menu.orderSettingsDisclaimerBottom') }}
            </p>
        </v-row>
    </v-container>
</template>
<style scoped></style>
